/**
 * ADVANCED (DYNAMIC) PHYSICS ANALYZER — the "why is this tower in trouble?" brain.
 *
 * The quasi-static solver (solver.ts) answers *whether* a member is overloaded.
 * This module answers *why a tower is dynamically dangerous*: it detects the
 * time-dependent aeroelastic phenomena that a static utilisation check cannot
 * see — resonance, vortex-induced vibration (VIV) lock-in, galloping, dynamic
 * gust amplification, and VIV-driven fatigue — and emits player-readable,
 * CITED events keyed to the phenomena catalog (physics/phenomena.ts).
 *
 * It is ADVISORY: these events explain and warn. They are layered *on top of*
 * the existing quasi-static failure model and (for now) do not themselves
 * amplify loads or cause failure — that wiring is a described follow-up
 * (see docs/ADVANCED_PHYSICS.md "Follow-up").
 *
 * Everything is SI (m, kg, s, Hz, N, Pa). DETERMINISTIC: no Math.random(), no
 * argless Date — every call takes explicit inputs, so replays reproduce exactly.
 *
 * FIRST-PRINCIPLES SOURCES (traced, not just cited):
 *  - docs/engineering/02_gust_and_dynamics.md — Davenport GLF, f1 of cantilevers
 *    (eq. E2: f1 = 0.56/L²·√(EI/m)), damping, DAF = 1/√((1-r²)²+(2ζr)²),
 *    rigid/flexible split at f1 = 1 Hz.
 *  - docs/engineering/03_aeroelastic_instability.md — Strouhal fs = St·V/D,
 *    VIV lock-in Vcrit = fn·D/St, Scruton Sc = 2mζ/ρD², Griffin plot (Sc≲10–20),
 *    Den Hartog galloping dCL/dα + CD < 0, S–N/Miner fatigue.
 *  - docs/engineering/11_modal_harmonics.md — modal/harmonic-coincidence checks
 *    (fs, 2fs, gust peak vs any mode fn; closely-spaced-mode / beating flag),
 *    Rayleigh-quotient f1, Miner cycle count ni = fn·t.
 */

import type { BuiltTower } from "@/game/towerSpec";
import type { Structure } from "@/physics/model";
import {
  STEEL_DAMPING,
  FLEXIBLE_FREQ_HZ,
  dynamicAmplification,
  isFlexible,
  estimateFundamentalHz as estimateFundamentalHzFromHeight,
} from "@/physics/gustModel";

// ---------------------------------------------------------------------------
// Physical constants
// ---------------------------------------------------------------------------

/** Air density ρ at sea level (kg/m³) — used in Strouhal/Scruton (doc 03). */
export const AIR_DENSITY = 1.225;
/** Kinematic viscosity of air ν (m²/s) — for the Reynolds/Strouhal regime note. */
export const AIR_KINEMATIC_VISCOSITY = 1.5e-5;
/** Strouhal number for a circular cylinder in the subcritical range (doc 03 §1). */
export const STROUHAL_CIRCULAR = 0.2;

/**
 * VIV lock-in Scruton threshold. Below this, the Griffin plot (doc 03 §2) shows
 * VIV amplitudes are large (~0.1–1.0 D) and the lock-in band is wide; above it
 * VIV is negligible and codes let you skip the check. Sc ≈ 10–20 is the classic
 * screening band; we take 20 as the "not clearly safe" ceiling and 10 as the
 * "definitely dangerous" floor.
 */
export const SCRUTON_LOCKIN_CEILING = 20;
export const SCRUTON_DANGER_FLOOR = 10;

/**
 * Resonance-coincidence tolerance: an excitation harmonic lands "on" a mode when
 * within ±TOL of it (doc 11 §3 suggests ±15%). VIV lock-in itself synchronises
 * over a wider band (±25–40% of Vcrit, doc 03 §2), captured separately below.
 */
export const RESONANCE_TOL = 0.15;
/** VIV lock-in band half-width as a fraction of Vcrit (doc 03 §2: ±25–40%). */
export const LOCKIN_BAND = 0.3;

/** Peak (3-s gust) equivalent frequency band the gust spectrum can excite (Hz).
 *  Gust energy tails off above ~1–2 Hz (doc 02 §2 / doc 11 §1.1); we treat the
 *  sharp-gust-front content (~0.6 Hz octave in gustModel) as the top of the band. */
export const GUST_EXCITATION_HZ = 0.6;

// ---------------------------------------------------------------------------
// Pure helpers (all deterministic, no state)
// ---------------------------------------------------------------------------

/**
 * Strouhal vortex-shedding frequency fs = St·V/D (doc 03 §1). V is the mean wind
 * speed (m/s), D the across-wind reference dimension (m). St defaults to 0.2
 * (circular cylinder, subcritical). Returns 0 for a degenerate diameter.
 */
export function strouhalFrequency(V: number, diameter: number, St = STROUHAL_CIRCULAR): number {
  if (diameter <= 0) return 0;
  return (St * V) / diameter;
}

/**
 * Lock-in critical wind speed Vcrit = fn·D/St (doc 03 §2): the mean wind at which
 * vortex shedding meets the structural frequency fn and can synchronise.
 */
export function lockInWindSpeed(fn: number, diameter: number, St = STROUHAL_CIRCULAR): number {
  if (St <= 0) return Infinity;
  return (fn * diameter) / St;
}

/**
 * Scruton (mass–damping) number Sc = 2·m·ζ / (ρ·D²) (doc 03 §2). m = mass per
 * unit length (kg/m), ζ = damping ratio, ρ = air density, D = diameter. High Sc
 * ⇒ small VIV; low Sc ⇒ large VIV / wide lock-in.
 */
export function scrutonNumber(m: number, zeta: number, rho: number, D: number): number {
  if (D <= 0 || rho <= 0) return Infinity;
  return (2 * m * zeta) / (rho * D * D);
}

/**
 * Reynolds number Re = V·D/ν (doc 03 §1) — decides the Strouhal regime, mostly
 * informational (St ≈ 0.2 holds across the common subcritical band).
 */
export function reynoldsNumber(V: number, D: number, nu = AIR_KINEMATIC_VISCOSITY): number {
  if (nu <= 0) return 0;
  return (V * D) / nu;
}

/**
 * Den Hartog galloping stability. Total across-wind damping goes negative — the
 * wind feeds energy into the motion — when dCL/dα + CD < 0 (doc 03 §3). Circular
 * clean sections have dCL/dα = 0 and never gallop; ice turns a round guy into an
 * asymmetric section with strongly negative lift slope. Returns whether the
 * criterion is VIOLATED (unstable) and, if so, the onset wind speed
 * Vg = -4·m·ζ·ωn / (ρ·D·(dCL/dα + CD)) above which galloping grows unbounded.
 */
export function denHartogGalloping(params: {
  dCLdAlpha: number;
  CD: number;
  m: number; // mass per unit length (kg/m)
  zeta: number;
  fn: number; // Hz
  D: number;
  rho?: number;
}): { unstable: boolean; slope: number; onsetWindSpeed: number } {
  const { dCLdAlpha, CD, m, zeta, fn, D, rho = AIR_DENSITY } = params;
  const slope = dCLdAlpha + CD; // Den Hartog slope; < 0 ⇒ unstable
  const unstable = slope < 0;
  if (!unstable || D <= 0 || rho <= 0) {
    return { unstable, slope, onsetWindSpeed: Infinity };
  }
  const omega = 2 * Math.PI * fn;
  const onsetWindSpeed = -(4 * m * zeta * omega) / (rho * D * slope);
  return { unstable, slope, onsetWindSpeed };
}

/**
 * Resonance-coincidence check (doc 11 §3, eq. 10–11): does an excitation
 * frequency f_excite land within ±tol of ANY mode? Returns the closest mode and
 * its fractional detuning; `hit` is true when |Δf|/fn ≤ tol.
 */
export function resonanceCoincidence(
  f_excite: number,
  modes: readonly number[],
  tol = RESONANCE_TOL,
): { hit: boolean; nearestMode: number; detuning: number } {
  let nearestMode = 0;
  let best = Infinity;
  for (const fn of modes) {
    if (fn <= 0) continue;
    const detune = Math.abs(f_excite - fn) / fn;
    if (detune < best) {
      best = detune;
      nearestMode = fn;
    }
  }
  return { hit: best <= tol, nearestMode, detuning: best };
}

// ---------------------------------------------------------------------------
// estimateFundamentalHz — a structure-aware Rayleigh estimate, with fallback
// ---------------------------------------------------------------------------

/**
 * Best-available fundamental frequency f1 (Hz) for a built tower.
 *
 * METHOD (doc 02 §4 eq. E2 / doc 11 §1.1 eq. 6): treat the tower as an
 * equivalent uniform cantilever and use the closed-form first-mode frequency
 *
 *     f1 = (0.560 / L²)·√(EI / m̄)
 *
 * where L = height, m̄ = mass per unit length (total structural mass / L), and
 * EI = equivalent lateral bending stiffness. For a lattice/space-truss the
 * bending stiffness comes from the *chord couple* (the legs acting as flanges):
 *
 *     I ≈ Σ_leg A_leg · r²      (parallel-axis; r = leg radius from centreline)
 *
 * measured on the base ring (the stiffest, load-governing section). This is a
 * Rayleigh-style stiffness estimate from the actual member layout, so it tracks
 * base width, leg area and steel grade — a genuine improvement over the
 * height-only rule f1 ≈ 1/(0.019·H^0.75) when a base ring is resolvable.
 *
 * Guyed masts break the clean-cantilever assumption (guy stiffness dominates the
 * low modes, doc 02 §4 / doc 11 §1.2), so for guyed towers we DO NOT trust the
 * bare-cantilever number and fall back to the height-only estimate, which is the
 * conservative, better-behaved choice there.
 *
 * Falls back to the height-only estimate whenever the structure-based number is
 * not computable (no base ring, zero mass, non-finite result).
 */
export function estimateFundamentalHz(built: BuiltTower): number {
  const fallback = estimateFundamentalHzFromHeight(built.height);

  // Guyed masts: guy stiffness dominates; the clean-cantilever form does not
  // apply. Use the height-only estimate (documented above).
  if (built.type === "guyed") return fallback;

  const L = built.height;
  if (!(L > 0)) return fallback;

  const s: Structure = built.structure;

  // Total structural mass and mass per unit length m̄.
  let totalMass = 0;
  for (const n of s.nodes) totalMass += n.mass;
  if (!(totalMass > 0)) return fallback;
  const mBar = totalMass / L;

  // Equivalent bending stiffness EI from the base-ring chord couple.
  const baseRing = built.render.rings[0];
  if (!baseRing || baseRing.length < 2) return fallback;

  // Pick a representative Young's modulus from a leg member if we can find one;
  // default to structural steel (200 GPa) otherwise.
  let E = 200e9;
  // Sum A·r² over the base-ring legs (their axial area × radius² = the couple).
  let I = 0;
  for (const idx of baseRing) {
    const node = s.nodes[idx];
    if (!node) continue;
    const r2 = node.x * node.x + node.z * node.z;
    // Find a member incident on this base node that rises (a leg): use its area.
    let legArea = 0;
    for (const m of s.members) {
      if (m.tensionOnly) continue;
      if (m.i === idx || m.j === idx) {
        const other = m.i === idx ? s.nodes[m.j] : s.nodes[m.i];
        if (other && other.y > node.y + 1e-6) {
          legArea = m.section.A;
          E = m.material.E;
          break;
        }
      }
    }
    I += legArea * r2;
  }
  if (!(I > 0)) return fallback;

  const EI = E * I;
  const f1 = (0.56 / (L * L)) * Math.sqrt(EI / mBar);
  if (!Number.isFinite(f1) || f1 <= 0) return fallback;
  return f1;
}

/**
 * Across-wind reference diameter D (m) the wind "sees" for shedding: the base
 * footprint width for a lattice/guyed mast, or the tube diameter for a
 * shell-clad monopole (the real bluff body that sheds vortices).
 */
function referenceDiameter(built: BuiltTower): number {
  // Shell (monopole tube): use the mean of base/top outer diameter.
  // render.tubeRadius holds per-level OUTER radii; use the base one ×2.
  const tube = built.render.tubeRadius;
  if (tube && tube.length > 0 && tube[0] !== undefined && tube[0] > 0) {
    return tube[0] * 2;
  }
  // Otherwise the base footprint width (2 × largest base-ring radius).
  const baseRing = built.render.rings[0];
  if (baseRing && baseRing.length > 0) {
    let r = 0;
    for (const idx of baseRing) {
      const n = built.structure.nodes[idx];
      if (n) r = Math.max(r, Math.hypot(n.x, n.z));
    }
    if (r > 0) return r * 2;
  }
  return Math.max(0.1, built.halfWidth * 2);
}

/** Total structural mass (kg) of a built tower — Σ node masses. */
function totalStructuralMass(built: BuiltTower): number {
  let m = 0;
  for (const n of built.structure.nodes) m += n.mass;
  return m;
}

// ---------------------------------------------------------------------------
// The stateful analyzer
// ---------------------------------------------------------------------------

/** Player-facing dynamic event, keyed to a phenomena.ts catalog id. */
export interface PhenomenonEvent {
  /** A key of PHENOMENA in physics/phenomena.ts (e.g. "resonance"). */
  phenomenonId: string;
  severity: "info" | "warn" | "critical";
  title: string;
  detail: string;
}

/** The distinct detector conditions we debounce on (one active flag each). */
type DetectorKey =
  | "resonance"
  | "viv"
  | "galloping"
  | "gust-amplification"
  | "fatigue";

/** Optional per-tower context the game can supply (defaults are sensible). */
export interface DynamicsContext {
  /** Structural damping ratio ζ (default STEEL_DAMPING ≈ 1.5% for bolted steel). */
  zeta?: number;
  /** Iced / asymmetric section? Enables the (conditional) galloping detector. */
  iced?: boolean;
  /**
   * Den Hartog lift-slope + drag for the (iced/asymmetric) section. Only used
   * when `iced` is true. A thin-ice crescent on a round guy is strongly negative
   * (doc 03 §3); default −4 (dCL/dα) with CD ≈ 1.2 gives a clearly unstable −2.8.
   */
  dCLdAlpha?: number;
  CD?: number;
  /** Higher modes (Hz) to also screen against, beyond f1 (doc 11 multi-mode). */
  extraModes?: readonly number[];
}

/**
 * DynamicsAnalyzer — construct ONE per tower, then call `update()` each frame
 * with the instantaneous (gusting) wind and the current mean wind. It returns
 * zero or more PhenomenonEvents, DEBOUNCED so an ongoing condition emits once at
 * onset (and again if it worsens to a higher severity), not every frame.
 *
 * See docs/ADVANCED_PHYSICS.md for the exact per-frame integration hook.
 */
export class DynamicsAnalyzer {
  readonly f1: number;
  readonly modes: number[];
  readonly diameter: number;
  readonly massPerLength: number;
  readonly zeta: number;
  readonly scruton: number;
  private readonly ctx: DynamicsContext;

  /** Debounce state: severity currently latched per detector (null = inactive). */
  private readonly active: Map<DetectorKey, PhenomenonEvent["severity"] | null> = new Map();

  /** Sustained-VIV dwell time (s) for the fatigue hint. */
  private vivDwell = 0;
  private fatigueEmitted = false;

  constructor(built: BuiltTower, ctx: DynamicsContext = {}) {
    this.ctx = ctx;
    this.zeta = ctx.zeta ?? STEEL_DAMPING;
    this.f1 = estimateFundamentalHz(built);
    this.diameter = referenceDiameter(built);
    const L = Math.max(1e-6, built.height);
    this.massPerLength = totalStructuralMass(built) / L;
    this.scruton = scrutonNumber(this.massPerLength, this.zeta, AIR_DENSITY, this.diameter);
    this.modes = [this.f1, ...(ctx.extraModes ?? [])].filter((f) => f > 0);
    for (const k of ["resonance", "viv", "galloping", "gust-amplification", "fatigue"] as const) {
      this.active.set(k, null);
    }
  }

  /**
   * Per-frame update. `windSpeed` = instantaneous (gusting) speed the solver is
   * applying this step; `meanWindSpeed` = the sustained mean (drives shedding
   * frequency & lock-in, which key off the MEAN, not the gust); `dt` = seconds
   * since the last update (for fatigue dwell accumulation).
   */
  update(windSpeed: number, meanWindSpeed: number, dt: number): PhenomenonEvent[] {
    const events: PhenomenonEvent[] = [];
    const fs = strouhalFrequency(meanWindSpeed, this.diameter);

    // (a) RESONANCE — gust peak or a vortex harmonic (fs or 2fs) lands on a mode.
    this.detectResonance(fs, meanWindSpeed, events);

    // (b) VIV LOCK-IN — fs ≈ f1 AND Scruton low.
    const vivActive = this.detectVIV(fs, meanWindSpeed, events);

    // (c) GALLOPING — Den Hartog violated above onset wind (iced/asymmetric).
    this.detectGalloping(meanWindSpeed, events);

    // (d) GUST AMPLIFICATION — flexible (f1<1 Hz) and DAF significant.
    this.detectGustAmplification(windSpeed, meanWindSpeed, events);

    // (e) FATIGUE — sustained VIV dwell accumulates cycles.
    this.detectFatigue(vivActive, fs, dt, events);

    return events;
  }

  // --- individual detectors -------------------------------------------------

  private detectResonance(fs: number, meanWind: number, out: PhenomenonEvent[]): void {
    // Screen the three excitation harmonics against every mode (doc 11 §3).
    const lift = resonanceCoincidence(fs, this.modes);
    const drag = resonanceCoincidence(2 * fs, this.modes);
    const gust = resonanceCoincidence(GUST_EXCITATION_HZ, this.modes);

    let severity: PhenomenonEvent["severity"] | null = null;
    let source = "";
    let f_excite = 0;
    let mode = 0;
    if (fs > 0 && lift.hit) {
      severity = "critical";
      source = "vortex shedding (fs)";
      f_excite = fs;
      mode = lift.nearestMode;
    } else if (fs > 0 && drag.hit) {
      severity = "warn";
      source = "the drag harmonic (2·fs)";
      f_excite = 2 * fs;
      mode = drag.nearestMode;
    } else if (gust.hit && this.f1 < FLEXIBLE_FREQ_HZ) {
      severity = "warn";
      source = "the gust spectral peak";
      f_excite = GUST_EXCITATION_HZ;
      mode = gust.nearestMode;
    }

    if (severity && this.transition("resonance", severity)) {
      const kmh = Math.round(meanWind * 3.6);
      out.push({
        phenomenonId: "resonance",
        severity,
        title: `Resonance: ${source} at ${kmh} km/h is near the ${mode.toFixed(2)} Hz mode`,
        detail:
          `${source} at ${f_excite.toFixed(2)} Hz has landed within ` +
          `${Math.round(RESONANCE_TOL * 100)}% of a natural mode (${mode.toFixed(2)} Hz). ` +
          `Each cycle adds to the last, so sway amplifies far beyond the static ` +
          `deflection (amplification ≈ 1/(2ζ) ≈ ${Math.round(1 / (2 * this.zeta))}× at ζ=${(this.zeta * 100).toFixed(1)}%). ` +
          `Detune the structure or add damping.`,
      });
    } else if (!severity) {
      this.transition("resonance", null);
    }
  }

  private detectVIV(fs: number, meanWind: number, out: PhenomenonEvent[]): boolean {
    if (fs <= 0 || this.f1 <= 0) {
      this.transition("viv", null);
      return false;
    }
    const vcrit = lockInWindSpeed(this.f1, this.diameter);
    // Lock-in synchronises over ±LOCKIN_BAND about Vcrit (doc 03 §2), the band
    // widening as Scruton falls. Only a low-Scruton structure actually locks in.
    const inBand = Math.abs(meanWind - vcrit) <= LOCKIN_BAND * vcrit;
    const lowScruton = this.scruton <= SCRUTON_LOCKIN_CEILING;
    if (!inBand || !lowScruton) {
      this.transition("viv", null);
      return false;
    }
    const severity: PhenomenonEvent["severity"] =
      this.scruton <= SCRUTON_DANGER_FLOOR ? "critical" : "warn";
    if (this.transition("viv", severity)) {
      const kmh = Math.round(meanWind * 3.6);
      out.push({
        phenomenonId: "vortex-induced-vibration",
        severity,
        title: `Vortex shedding at ${kmh} km/h is locking onto the ${this.f1.toFixed(2)} Hz sway mode`,
        detail:
          `Near the lock-in wind Vcrit = f1·D/St ≈ ${Math.round(vcrit * 3.6)} km/h, shedding ` +
          `abandons the Strouhal line and synchronises to the structure's ${this.f1.toFixed(2)} Hz ` +
          `mode, driving steady across-wind sway. Scruton number Sc = 2mζ/ρD² ≈ ${this.scruton.toFixed(1)} ` +
          `(< ${SCRUTON_LOCKIN_CEILING}: lock-in amplitudes are significant; ` +
          `${severity === "critical" ? `< ${SCRUTON_DANGER_FLOOR}: large ~0.1–1.0 D amplitudes` : "add mass/damping or strakes to raise Sc"}). ` +
          `This is the dominant across-wind fatigue driver for light, lightly-damped masts.`,
      });
    }
    return true;
  }

  private detectGalloping(meanWind: number, out: PhenomenonEvent[]): void {
    if (!this.ctx.iced) {
      this.transition("galloping", null);
      return;
    }
    const dh = denHartogGalloping({
      dCLdAlpha: this.ctx.dCLdAlpha ?? -4,
      CD: this.ctx.CD ?? 1.2,
      m: this.massPerLength,
      zeta: this.zeta,
      fn: this.f1,
      D: this.diameter,
    });
    if (!dh.unstable || !(meanWind >= dh.onsetWindSpeed)) {
      this.transition("galloping", null);
      return;
    }
    if (this.transition("galloping", "critical")) {
      out.push({
        phenomenonId: "galloping",
        severity: "critical",
        title: `Galloping: iced section is unstable above ${Math.round(dh.onsetWindSpeed * 3.6)} km/h`,
        detail:
          `Ice has made the section asymmetric, so the Den Hartog slope dCL/dα + CD = ` +
          `${dh.slope.toFixed(1)} < 0 — the wind feeds energy into the motion. Above the onset ` +
          `Vg = −4mζωn/(ρD·slope) ≈ ${Math.round(dh.onsetWindSpeed * 3.6)} km/h, low-frequency ` +
          `across-wind swings grow without bound and can overload anchors/members. ` +
          `CONDITIONAL: applies only while the ice/asymmetric shape persists.`,
      });
    }
  }

  private detectGustAmplification(
    windSpeed: number,
    meanWind: number,
    out: PhenomenonEvent[],
  ): void {
    if (!isFlexible(this.f1)) {
      this.transition("gust-amplification", null);
      return;
    }
    const T1 = 1 / this.f1;
    const daf = dynamicAmplification(T1, 3.0, this.zeta);
    // "Significant" ⇒ meaningfully above the quasi-static 1.0. Warn ≥1.5, crit ≥3.
    let severity: PhenomenonEvent["severity"] | null = null;
    if (daf >= 3) severity = "critical";
    else if (daf >= 1.5) severity = "warn";
    if (severity && this.transition("gust-amplification", severity)) {
      const kmh = Math.round(meanWind * 3.6);
      const gustKmh = Math.round(windSpeed * 3.6);
      out.push({
        phenomenonId: "gust-amplification",
        severity,
        title: `Gust amplification: flexible mast (f1 ${this.f1.toFixed(2)} Hz) is resonating with the gusts`,
        detail:
          `The tower is flexible (f1 = ${this.f1.toFixed(2)} Hz < ${FLEXIBLE_FREQ_HZ} Hz), so gust ` +
          `energy near its natural frequency is dynamically amplified: DAF = 1/√((1−r²)²+(2ζr)²) ` +
          `≈ ${daf.toFixed(1)}× at ζ=${(this.zeta * 100).toFixed(1)}%. A gust to ${gustKmh} km/h over the ` +
          `${kmh} km/h mean therefore hits harder than its static pressure implies (Davenport's ` +
          `resonant term R ∝ 1/ζ). Stiffer, shorter, or better-damped structures avoid it.`,
      });
    } else if (!severity) {
      this.transition("gust-amplification", null);
    }
  }

  private detectFatigue(
    vivActive: boolean,
    fs: number,
    dt: number,
    out: PhenomenonEvent[],
  ): void {
    if (!vivActive) {
      // Fatigue is a slow-burn: we keep the accumulated dwell (don't reset) but
      // stop accruing while VIV is not active.
      this.transition("fatigue", null);
      return;
    }
    this.vivDwell += Math.max(0, dt);
    // Cycle count ni = fn·t (doc 11 §5 eq. 16). Emit a hint once enough cycles
    // have accumulated under sustained lock-in (~a few thousand cycles is where
    // welded-detail damage starts to matter for a game-scale slow burn).
    const cycles = fs * this.vivDwell;
    const FATIGUE_CYCLE_HINT = 3000;
    if (cycles >= FATIGUE_CYCLE_HINT && !this.fatigueEmitted) {
      this.fatigueEmitted = true;
      this.active.set("fatigue", "warn");
      out.push({
        phenomenonId: "fatigue",
        severity: "warn",
        title: `Fatigue: sustained vortex-induced vibration has run up ~${Math.round(cycles).toLocaleString("en-US")} stress cycles`,
        detail:
          `VIV lock-in has driven the ${this.f1.toFixed(2)} Hz mode for ${Math.round(this.vivDwell)} s, ` +
          `accumulating ni = fn·t ≈ ${Math.round(cycles).toLocaleString("en-US")} cycles. Because the S–N law ` +
          `gives N ∝ Δσ⁻³ (welded steel, m≈3), each cycle eats connection life via Palmgren–Miner ` +
          `(D = Σ ni/Ni → 1). Welds, flange bolts and base-plate/anchor details are the hot spots — ` +
          `this is why across-wind response governs connection design even when static strength is ample.`,
      });
    }
  }

  // --- debounce helper ------------------------------------------------------

  /**
   * Latch a detector's severity. Returns true only when we should EMIT: on first
   * activation, or when severity escalates (info→warn→critical). De-activation
   * (target null) or an unchanged/lower severity emits nothing. This is what
   * stops identical events spamming every frame.
   */
  private transition(key: DetectorKey, target: PhenomenonEvent["severity"] | null): boolean {
    const rank = (s: PhenomenonEvent["severity"] | null): number =>
      s === "critical" ? 3 : s === "warn" ? 2 : s === "info" ? 1 : 0;
    const cur = this.active.get(key) ?? null;
    if (target === null) {
      this.active.set(key, null);
      return false;
    }
    const emit = rank(target) > rank(cur);
    this.active.set(key, target);
    return emit;
  }
}
